import React, { useState, useEffect } from 'react';
import Header from './components/Header';
import Login from './components/Login';
import ClientDetails from './components/ClientDetails';
import ClientList from './components/ClientList';
import {
    createClient,
    getClient,
    evaluateCreditWithId,
    getCurrentUser,
    getAuthToken
} from './services/api';
import {
    AppView,
    CompanyInfo,
    User,
    UserSettings,
    Client
} from './types';

const App: React.FC = () => {
    const [currentView, setCurrentView] = useState<AppView>('list');
    const [currentClientId, setCurrentClientId] = useState<string | null>(null);
    const [companyInfo, setCompanyInfo] = useState<CompanyInfo>({
        companyName: '',
        industry: 'Technology',
        fiscalYear: new Date().getFullYear(),
        currency: 'USD',
        reportingPeriod: 'annual',
        scale: 'thousands'
    });
    const [client, setClient] = useState<Client | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);

    // Authentication state
    const [user, setUser] = useState<User | null>(null);
    const [userSettings, setUserSettings] = useState<UserSettings | null>(null);
    const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
    const [isCheckingAuth, setIsCheckingAuth] = useState<boolean>(true);

    // Check authentication on app load
    useEffect(() => {
        const checkAuth = async () => {
            const token = getAuthToken();
            if (token) {
                try {
                    const response = await getCurrentUser();
                    if (response.success) {
                        setUser(response.user);
                        setUserSettings(response.settings);
                        setIsAuthenticated(true);
                    }
                } catch (error) {
                    console.error('Auth check failed:', error);
                }
            }
            setIsCheckingAuth(false);
        };

        checkAuth();
    }, []);

    const handleLoginSuccess = (userData: User, settings: UserSettings) => {
        setUser(userData);
        setUserSettings(settings);
        setIsAuthenticated(true);
        setError(null);
    };

    const handleLogout = () => {
        setUser(null);
        setUserSettings(null);
        setIsAuthenticated(false);
        setCurrentView('list');
        setCurrentClientId(null);
        resetForm();
    };

    const handleSettingsUpdate = (settings: UserSettings) => {
        setUserSettings(settings);
        // Update company info defaults if not on list view and it's a new client
        if (currentView !== 'list' && !currentClientId) {
            setCompanyInfo(prev => ({
                ...prev,
                industry: settings.default_industry,
                currency: settings.default_currency
            }));
        }
    };

    const resetForm = (): void => {
        setCompanyInfo({
            companyName: '',
            industry: userSettings?.default_industry || 'Technology',
            fiscalYear: new Date().getFullYear(),
            currency: userSettings?.default_currency || 'USD',
            reportingPeriod: 'annual',
            scale: 'thousands'
        });
        setClient(null);
        setError(null);
        setCurrentClientId(null);
    };

    const handleNewClient = async (): Promise<void> => {
        resetForm();
        setClient(null);
        setCurrentClientId(null);
        setCurrentView('results'); // Use 'results' view name but ClientDetails handles new clients
    };

    const handleSelectClient = async (clientId: string): Promise<void> => {
        try {
            setIsLoading(true);
            setError(null);

            const response = await getClient(clientId);
            const client = response.client;

            // Load client data into companyInfo for both draft and completed clients
            setCompanyInfo({
                companyName: client.company_name,
                industry: client.industry,
                fiscalYear: client.fiscal_year,
                currency: client.currency,
                reportingPeriod: client.reporting_period,
                scale: client.scale
            });

            // Set client and view - always use ClientDetails component
            setClient(client);
            setCurrentView('results'); // Use 'results' view name but ClientDetails handles both draft and completed
            setCurrentClientId(clientId);
        } catch (err) {
            setError((err as Error).message || 'Failed to load client');
        } finally {
            setIsLoading(false);
        }
    };

    const handleCompanyInfoChange = (field: keyof CompanyInfo, value: string | number): void => {
        setCompanyInfo(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleEvaluate = async (): Promise<void> => {
        if (!currentClientId) {
            setError("No client ID found");
            return;
        }

        setIsLoading(true);
        setError(null);

        try {
            const updatedClient = await evaluateCreditWithId(currentClientId);
            setClient(updatedClient);
            // Refresh client data to get updated status
            const response = await getClient(currentClientId);
            setClient(response.client);
        } catch (err) {
            setError((err as Error).message || 'An error occurred during evaluation');
        } finally {
            setIsLoading(false);
        }
    };

    const handleBackToList = (): void => {
        resetForm();
        setCurrentView('list');
    };

    const handleStartNewClient = async (): Promise<void> => {
        try {
            setIsLoading(true);
            setError(null);

            // Create client in backend first
            const response = await createClient(companyInfo);
            setCurrentClientId(response.client.id);
            setClient(response.client);
        } catch (err) {
            setError((err as Error).message || 'Failed to create client');
        } finally {
            setIsLoading(false);
        }
    };

    const canEvaluate = (): boolean => {
        return companyInfo.companyName !== '';
    };

    // Show loading screen while checking authentication
    if (isCheckingAuth) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
                    <p className="mt-4 text-gray-600">Loading...</p>
                </div>
            </div>
        );
    }

    // Show login screen if not authenticated
    if (!isAuthenticated) {
        return (
            <Login
                onLoginSuccess={handleLoginSuccess}
            />
        );
    }

    // Show main application if authenticated
    if (currentView === 'list') {
        return (
            <div className="min-h-screen bg-gray-50">
                <Header
                    user={user}
                    userSettings={userSettings}
                    onLogout={handleLogout}
                    onSettingsUpdate={handleSettingsUpdate}
                />
                <ClientList
                    onSelectClient={handleSelectClient}
                    onNewClient={handleNewClient}
                />
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50">
            <Header
                user={user}
                userSettings={userSettings}
                onLogout={handleLogout}
                onSettingsUpdate={handleSettingsUpdate}
            />

            <div className="container mx-auto px-4 py-8">
                {/* Breadcrumb Navigation */}
                <nav className="mb-6">
                    <ol className="flex items-center space-x-2 text-sm text-gray-600">
                        <li>
                            <button
                                onClick={handleBackToList}
                                className="hover:text-blue-600 transition-colors"
                                type="button"
                            >
                                Clients
                            </button>
                        </li>
                        <li>/</li>
                        <li className="text-gray-900">
                            {client ? client.company_name : (currentClientId ? 'Edit Client' : 'New Client')}
                        </li>
                    </ol>
                </nav>

                {currentView === 'results' && (
                    <ClientDetails
                        client={client}
                        companyInfo={companyInfo}
                        onCompanyInfoChange={handleCompanyInfoChange}
                        onEvaluate={handleEvaluate}
                        onStartNewClient={handleStartNewClient}
                        onReset={handleBackToList}
                        isLoading={isLoading}
                        error={error}
                        canEvaluate={canEvaluate()}
                        isNewClient={!currentClientId}
                        currentClientId={currentClientId}
                        onClientUpdate={(updatedClient) => {
                            setClient(updatedClient);
                        }}
                    />
                )}
            </div>
        </div>
    );
};

export default App;
